import type { ReactNode } from 'react'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  TableProperties,
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import type { KeywordCoverageSort, SortDirection } from '@/api/keywordCoverage/api'
import type { KeywordCoverageRow } from '@/api/keywordCoverage/types'
import { useKeywordCoverage } from '@/features/keywordCoverage/useKeywordCoverage'
import { KeywordAreaNav } from '@/features/keywords/KeywordAreaNav'

const numberFormatter = new Intl.NumberFormat('de-DE')
const pageSizeOptions = [25, 50, 100, 500] as const
type PageSize = (typeof pageSizeOptions)[number]

function parsePage(value: string | null): number {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

function parsePageSize(value: string | null): PageSize {
  const parsed = Number(value)
  return pageSizeOptions.find((option) => option === parsed) ?? 50
}

function parseSort(value: string | null): KeywordCoverageSort {
  switch (value) {
    case 'total':
    case 'primary':
    case 'secondary':
    case 'emotion':
    case 'longtail':
    case 'alias':
      return value
    default:
      return 'sales'
  }
}

function parseDirection(value: string | null, sort: KeywordCoverageSort): SortDirection {
  if (value === 'asc' || value === 'desc') return value
  return sort === 'sales' ? 'desc' : 'asc'
}

function SortableHeader({
  column,
  label,
  sort,
  direction,
  align = 'center',
  onSortChange,
}: {
  readonly column: KeywordCoverageSort
  readonly label: string
  readonly sort: KeywordCoverageSort
  readonly direction: SortDirection
  readonly align?: 'center' | 'right'
  readonly onSortChange: (sort: KeywordCoverageSort) => void
}): ReactNode {
  const isActive = sort === column

  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-center'}`}>
      <button
        type="button"
        onClick={() => {
          onSortChange(column)
        }}
        className={`inline-flex items-center gap-1.5 hover:text-blue-700 dark:hover:text-blue-300 ${
          align === 'right' ? 'justify-end' : 'justify-center'
        }`}
      >
        {label}
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUp aria-label="Aufsteigend" size={14} />
          ) : (
            <ArrowDown aria-label="Absteigend" size={14} />
          )
        ) : null}
      </button>
    </th>
  )
}

function CountCell({ count }: { readonly count: number }): ReactNode {
  return count === 0 ? (
    <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 dark:bg-red-950 dark:text-red-300">
      Fehlt
    </span>
  ) : (
    <span className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
      {numberFormatter.format(count)}
    </span>
  )
}

function CoveragePagination({
  page,
  pageSize,
  showDetails,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onShowDetailsChange,
}: {
  readonly page: number
  readonly pageSize: PageSize
  readonly showDetails: boolean
  readonly totalPages: number
  readonly onPageChange: (page: number) => void
  readonly onPageSizeChange: (pageSize: PageSize) => void
  readonly onShowDetailsChange: (showDetails: boolean) => void
}): ReactNode {
  return (
    <nav className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => {
          onPageChange(page - 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200"
      >
        <ChevronLeft aria-hidden="true" size={17} />
        Zurück
      </button>

      <div className="flex flex-wrap items-center justify-center gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Spalten
          <select
            value={showDetails ? 'details' : 'keywords'}
            onChange={(event) => {
              onShowDetailsChange(event.target.value === 'details')
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="keywords">Keyword-Zahlen</option>
            <option value="details">Mit Beschreibung und AI Instructions</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          Pro Seite
          <select
            value={pageSize}
            onChange={(event) => {
              onPageSizeChange(parsePageSize(event.target.value))
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          Seite {numberFormatter.format(page)} von {numberFormatter.format(totalPages)}
        </span>
      </div>

      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => {
          onPageChange(page + 1)
        }}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200"
      >
        Weiter
        <ChevronRight aria-hidden="true" size={17} />
      </button>
    </nav>
  )
}

function OptionalText({ value }: { readonly value: string | null }): ReactNode {
  return value === null || value.trim() === '' ? (
    <span className="font-semibold text-red-600 dark:text-red-300">Nicht gepflegt</span>
  ) : (
    <span>{value}</span>
  )
}

function CoverageRow({
  row,
  showDetails,
}: {
  readonly row: KeywordCoverageRow
  readonly showDetails: boolean
}): ReactNode {
  const editUrl = `https://panda-weltmaschine.de/product-data/producttypes?init_key=${encodeURIComponent(row.producttypeKey)}`
  const detailUrl = `/producttype-keywords/producttype/${encodeURIComponent(row.producttypeKey)}`

  return (
    <tr className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/60">
      <td className="px-4 py-3">
        <div className="flex min-w-56 flex-col items-start gap-2">
          <Link
            to={detailUrl}
            className="inline-flex items-center gap-2 font-bold text-slate-950 hover:text-blue-700 dark:text-white dark:hover:text-blue-300"
          >
            <span className="rounded-md bg-slate-950 px-2 py-1 font-mono text-xs text-white dark:bg-slate-100 dark:text-slate-950">
              {row.producttypeKey}
            </span>
            {row.producttypeName}
          </Link>
          <a
            href={editUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300"
          >
            In Weltmaschine bearbeiten
            <ExternalLink aria-hidden="true" size={13} />
          </a>
        </div>
      </td>
      {showDetails ? (
        <>
          <td className="max-w-md whitespace-normal px-4 py-3 align-top text-xs leading-5 text-slate-600 dark:text-slate-300">
            <OptionalText value={row.description} />
          </td>
          <td className="max-w-md whitespace-normal px-4 py-3 align-top text-xs leading-5 text-slate-600 dark:text-slate-300">
            <OptionalText value={row.aiInstructions} />
          </td>
        </>
      ) : null}
      <td className="px-4 py-3 text-right font-black tabular-nums text-emerald-700 dark:text-emerald-300">
        {numberFormatter.format(row.sales365)}
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.totalCount} />
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.primaryCount} />
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.secondaryCount} />
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.emotionCount} />
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.longtailCount} />
      </td>
      <td className="px-4 py-3 text-center">
        <CountCell count={row.aliasCount} />
      </td>
    </tr>
  )
}

export function KeywordCoveragePage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parsePage(searchParams.get('page'))
  const pageSize = parsePageSize(searchParams.get('limit'))
  const showDetails = searchParams.get('details') === '1'
  const sort = parseSort(searchParams.get('sort'))
  const direction = parseDirection(searchParams.get('direction'), sort)
  const { data, isLoading, isFetching, error, refetch } = useKeywordCoverage(
    page,
    pageSize,
    sort,
    direction,
  )
  const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / pageSize))

  const writeSearchParams = (
    nextPage: number,
    nextPageSize: PageSize,
    nextShowDetails: boolean,
    nextSort: KeywordCoverageSort,
    nextDirection: SortDirection,
  ): void => {
    const nextParams = new URLSearchParams()
    if (nextPage > 1) nextParams.set('page', String(nextPage))
    if (nextPageSize !== 50) nextParams.set('limit', String(nextPageSize))
    if (nextShowDetails) nextParams.set('details', '1')
    if (nextSort !== 'sales') nextParams.set('sort', nextSort)
    if (nextDirection !== (nextSort === 'sales' ? 'desc' : 'asc')) {
      nextParams.set('direction', nextDirection)
    }
    setSearchParams(nextParams)
  }

  const changePage = (nextPage: number): void => {
    writeSearchParams(nextPage, pageSize, showDetails, sort, direction)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <KeywordAreaNav />
      <section className="rounded-3xl bg-gradient-to-br from-slate-950 via-cyan-950 to-emerald-950 px-6 py-8 text-white shadow-xl sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-100">
              <TableProperties aria-hidden="true" size={15} />
              Keyword-Abdeckung
            </div>
            <h1 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
              Fehlende Produkttyp-Keywords erkennen
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-300 sm:text-base">
              Aktive Produkttypen absteigend nach Verkäufen. Fehlende Keyword-Arten sind sofort rot
              markiert. Deaktivierte, gelöschte und offline geschaltete Produkttypen fehlen bewusst.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              void refetch()
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold hover:bg-white/15"
          >
            <RefreshCw
              aria-hidden="true"
              size={16}
              className={isFetching ? 'animate-spin' : undefined}
            />
            Aktualisieren
          </button>
        </div>
      </section>

      {error !== null ? (
        <div className="mt-6 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <AlertTriangle aria-hidden="true" size={19} className="mt-0.5 shrink-0" />
          <p className="text-sm font-medium">
            {error instanceof ApiError || error instanceof ApiContractError
              ? error.message
              : 'Die Keyword-Abdeckung konnte nicht geladen werden.'}
          </p>
        </div>
      ) : null}

      <div className="mt-6">
        <CoveragePagination
          page={page}
          pageSize={pageSize}
          showDetails={showDetails}
          totalPages={totalPages}
          onPageChange={changePage}
          onPageSizeChange={(nextPageSize) => {
            writeSearchParams(1, nextPageSize, showDetails, sort, direction)
          }}
          onShowDetailsChange={(nextShowDetails) => {
            writeSearchParams(1, pageSize, nextShowDetails, sort, direction)
          }}
        />
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="px-4 py-3 text-left">Produkttyp</th>
              {showDetails ? (
                <>
                  <th className="min-w-80 px-4 py-3 text-left">Beschreibung</th>
                  <th className="min-w-80 px-4 py-3 text-left">AI Instructions</th>
                </>
              ) : null}
              <SortableHeader
                column="sales"
                label="Verkäufe"
                sort={sort}
                direction={direction}
                align="right"
                onSortChange={(nextSort) => {
                  const nextDirection =
                    nextSort === sort ? (direction === 'asc' ? 'desc' : 'asc') : 'desc'
                  writeSearchParams(1, pageSize, showDetails, nextSort, nextDirection)
                }}
              />
              {(['total', 'primary', 'secondary', 'emotion', 'longtail', 'alias'] as const).map(
                (column) => (
                  <SortableHeader
                    key={column}
                    column={column}
                    label={
                      {
                        total: 'Alle',
                        primary: 'Primär',
                        secondary: 'Sekundär',
                        emotion: 'Emotion',
                        longtail: 'Longtail',
                        alias: 'Alias',
                      }[column]
                    }
                    sort={sort}
                    direction={direction}
                    onSortChange={(nextSort) => {
                      const nextDirection =
                        nextSort === sort ? (direction === 'asc' ? 'desc' : 'asc') : 'asc'
                      writeSearchParams(1, pageSize, showDetails, nextSort, nextDirection)
                    }}
                  />
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={showDetails ? 10 : 8}
                  className="px-4 py-16 text-center text-slate-500"
                >
                  Produkttypen werden geladen …
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={showDetails ? 10 : 8}
                  className="px-4 py-16 text-center text-slate-500"
                >
                  Keine aktiven Produkttypen gefunden.
                </td>
              </tr>
            ) : (
              data?.items.map((row) => (
                <CoverageRow key={row.producttypeKey} row={row} showDetails={showDetails} />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-7">
        <CoveragePagination
          page={page}
          pageSize={pageSize}
          showDetails={showDetails}
          totalPages={totalPages}
          onPageChange={changePage}
          onPageSizeChange={(nextPageSize) => {
            writeSearchParams(1, nextPageSize, showDetails, sort, direction)
          }}
          onShowDetailsChange={(nextShowDetails) => {
            writeSearchParams(1, pageSize, nextShowDetails, sort, direction)
          }}
        />
      </div>
    </div>
  )
}
